import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Booking, Service } from '../types';
import {
  getBookings,
  supabase,
  getServices
} from '../services/supabase';
import './AdminDashboard.css';

const AdminDashboard: React.FC = () => {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [serviceMap, setServiceMap] = useState<Record<string, Service>>({});
  const [fetchError, setFetchError] = useState<string | null>(null);

  const sortBookings = useCallback((records: Booking[]) => {
    return [...records].sort((a, b) => {
      const aDate = new Date(`${a.scheduled_date}T${a.scheduled_time || '00:00'}`);
      const bDate = new Date(`${b.scheduled_date}T${b.scheduled_time || '00:00'}`);
      return aDate.getTime() - bDate.getTime();
    });
  }, []);

  // Fetch user and determine admin
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const svc = await import('../services/supabase');
        const maybe = await svc.getSessionUser();
        const prof = maybe?.profile ?? null;
        console.info('[AdminDashboard] fetched session/profile', { hasSession: !!maybe.session, role: prof?.role ?? null });
        setIsAdmin(prof?.role === 'admin');
      } catch (err) {
        console.error('[AdminDashboard] error fetching profile to determine admin', err);
        setIsAdmin(false);
      }
    };
    fetchUser();
  }, []);

  useEffect(() => {
    const loadServices = async () => {
      try {
        const svcList = await getServices();
        const lookup = svcList.reduce<Record<string, Service>>((acc, service) => {
          acc[service.id] = service;
          return acc;
        }, {});
        setServiceMap(lookup);
      } catch (err) {
        console.warn('[AdminDashboard] failed to preload services for lookup', err);
      }
    };
    loadServices();
  }, []);

  useEffect(() => {
    const fetchBookings = async () => {
      setLoading(true);
      setFetchError(null);
      try {
        if (isAdmin) {
          const { data: sessionData } = await supabase.auth.getSession();
          const token = sessionData?.session?.access_token;
          if (!token) {
            throw new Error('Missing auth token. Please sign in again.');
          }

          const response = await fetch('/api/admin-bookings', {
            headers: { Authorization: `Bearer ${token}` }
          });

          if (!response.ok) {
            let message = `Admin bookings request failed (${response.status})`;
            try {
              const body = await response.json();
              if (body?.error) message = body.error;
            } catch (_) {
              // Ignore body parse errors
            }
            throw new Error(message);
          }

          const body = await response.json();
          const records: Booking[] = Array.isArray(body?.bookings) ? body.bookings : [];
          setBookings(sortBookings(records));
        } else {
          const bookingsData = await getBookings(false);
          setBookings(sortBookings(bookingsData));
        }
      } catch (err) {
        console.error('Error fetching bookings:', err);
        setBookings([]);
        setFetchError(err instanceof Error ? err.message : 'Failed to load bookings');
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, [isAdmin, sortBookings]);

  // Real-time subscription to bookings table so the dashboard updates automatically
  useEffect(() => {
    if (!isAdmin) return;
    console.info('[AdminDashboard] subscribing to realtime bookings events');
    const channel = supabase
      .channel('public:bookings')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'bookings' }, (payload: any) => {
        try {
          console.debug('[AdminDashboard] realtime payload:', payload?.eventType || payload?.type || payload);
          const rec = (payload?.new) || (payload?.record) || null;
          const oldRec = (payload?.old) || null;

          setBookings(prev => {
            if (!rec && !oldRec) return prev;
            const id = rec?.id ?? oldRec?.id;
            if (!id) return prev;

            let next = prev;

            if (oldRec && !rec) {
              next = prev.filter(b => b.id !== id);
              return sortBookings(next);
            }

            const exists = prev.find(b => b.id === id);
            if (exists) {
              next = prev.map(b => (b.id === id ? ({ ...b, ...rec }) : b));
              return sortBookings(next as Booking[]);
            }

            next = [...prev, rec as Booking];
            return sortBookings(next as Booking[]);
          });
        } catch (e) {
          console.error('[AdminDashboard] error handling realtime payload', e);
        }
      })
      .subscribe();

    return () => {
      try {
        channel.unsubscribe();
        // also remove channel from client
        // @ts-ignore
        supabase.removeChannel?.(channel);
      } catch (e) { /* ignore */ }
    };
  }, [isAdmin, sortBookings]);

  const currencyFormatter = useMemo(() => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }), []);

  const totalRevenue = useMemo(() => bookings.reduce((sum, booking) => sum + (booking.total_amount || 0), 0), [bookings]);
  const totalRevenueDisplay = useMemo(() => currencyFormatter.format(totalRevenue), [currencyFormatter, totalRevenue]);
  const pendingBookings = useMemo(() => bookings.filter(b => b.status === 'pending'), [bookings]);
  const confirmedCount = useMemo(() => bookings.filter(b => b.status === 'confirmed').length, [bookings]);

  const handleStatusUpdate = async (bookingId: string, newStatus: Booking['status']) => {
    setFetchError(null);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) {
        throw new Error('Missing auth token. Please sign in again.');
      }

      const response = await fetch('/api/update-booking-status', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ id: bookingId, status: newStatus })
      });

      if (!response.ok) {
        let message = `Status update failed (${response.status})`;
        try {
          const body = await response.json();
          if (body?.error) message = body.error;
        } catch (_) {
          // Ignore body parse errors
        }
        throw new Error(message);
      }

      const body = await response.json();
      const updatedBooking = body?.booking as Booking | undefined;
      if (!updatedBooking) {
        throw new Error('Status update succeeded but no booking data was returned.');
      }

      setBookings(prev => sortBookings(prev.map(booking =>
        booking.id === bookingId ? { ...booking, ...updatedBooking } : booking
      )));
    } catch (err) {
      console.error('Error updating booking status:', err);
      setFetchError(err instanceof Error ? err.message : 'Failed to update booking status');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return '#ffa500';
      case 'confirmed': return 'var(--color-teal)';
      case 'completed': return '#4caf50';
      case 'cancelled': return '#f44336';
      default: return '#ccc';
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return '#4caf50';
      case 'pending': return '#ffa500';
      case 'failed': return '#f44336';
      default: return '#ccc';
    }
  };

  const renderBookingRow = (booking: Booking) => {
    const service = booking.service_id ? serviceMap[booking.service_id] : undefined;
    const serviceName = service?.name || 'Service unavailable';
    const serviceDuration = service?.duration ? `${service.duration} min` : null;
    const scheduledDate = new Date(`${booking.scheduled_date}T${booking.scheduled_time || '00:00'}`);
    const locationParts = [booking.address, booking.city, booking.state].filter(Boolean).join(', ');
    const isMobile = booking.location_type === 'mobile';
    const locationLabel = isMobile ? 'Mobile Service' : 'Shop Drop-off';
    const locationSummary = isMobile ? (locationParts || 'Address pending') : '7230 Jemmah Lane, Jacksonville, FL';
    const travelFee = booking.travel_fee ?? 0;
    const travelMiles = booking.travel_miles;
    const travelMilesDisplay = travelMiles != null ? `${travelMiles.toFixed(1)} mi` : '—';
    const notes = booking.notes?.trim() ? booking.notes : 'None';
    const amountDisplay = currencyFormatter.format(booking.total_amount || 0);

    return (
      <div key={booking.id} className="table-row">
        <div className="table-cell" data-label="Customer">
          <div className="customer-info">
            <div className="customer-name">{booking.guest_name || 'Guest'}</div>
            <div className="customer-contact">{booking.guest_email || '—'}</div>
            <div className="customer-contact">{booking.guest_phone || '—'}</div>
            <div className="customer-id">#{booking.id}</div>
          </div>
        </div>
        <div className="table-cell" data-label="Service">
          <div className="service-info">
            <div className="service-name">{serviceName}</div>
            {serviceDuration && <div className="service-meta">Approx. {serviceDuration}</div>}
            {booking.service_id && <div className="service-meta">ID {booking.service_id.slice(0, 8)}…</div>}
          </div>
        </div>
        <div className="table-cell" data-label="Date & Time">
          <div className="datetime-info">
            <div>{scheduledDate.toLocaleDateString()}</div>
            <div>{booking.scheduled_time || '—'}</div>
          </div>
        </div>
        <div className="table-cell" data-label="Location">
          <div className="location-info">
            <div className="location-label">{locationLabel}</div>
            <div className="location-address">{locationSummary}</div>
          </div>
        </div>
        <div className="table-cell" data-label="Travel">
          <div className="travel-info">
            <div className="travel-fee">{currencyFormatter.format(travelFee)}</div>
            <div className="travel-miles">{isMobile ? travelMilesDisplay : 'On-site'}</div>
          </div>
        </div>
        <div className="table-cell" data-label="Notes">
          <div className="notes-info">{notes}</div>
        </div>
        <div className="table-cell" data-label="Status">
          <span className="status-badge" style={{ backgroundColor: getStatusColor(booking.status) }}>{booking.status}</span>
        </div>
        <div className="table-cell" data-label="Invoice">
          <div className="amount">{amountDisplay}</div>
          <span className="payment-badge" style={{ backgroundColor: getPaymentStatusColor(booking.payment_status) }}>{booking.payment_status}</span>
        </div>
        <div className="table-cell" data-label="Actions">
          <div className="action-buttons">
            {booking.status === 'pending' && (
              <button onClick={() => handleStatusUpdate(booking.id, 'confirmed')} className="action-btn confirm-btn">Confirm</button>
            )}
            {booking.status === 'confirmed' && (
              <button onClick={() => handleStatusUpdate(booking.id, 'completed')} className="action-btn complete-btn">Complete</button>
            )}
            <button onClick={() => handleStatusUpdate(booking.id, 'cancelled')} className="action-btn cancel-btn">Cancel</button>
          </div>
        </div>
      </div>
    );
  };

  const renderTableHeader = () => (
    <div className="table-header">
      <div className="table-cell">Customer</div>
      <div className="table-cell">Service</div>
      <div className="table-cell">Date &amp; Time</div>
      <div className="table-cell">Location</div>
      <div className="table-cell">Travel</div>
      <div className="table-cell">Notes</div>
      <div className="table-cell">Status</div>
      <div className="table-cell">Invoice</div>
      <div className="table-cell">Actions</div>
    </div>
  );

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="container">
        <div className="dashboard-header">
          <h1 className="dashboard-title">Admin Dashboard</h1>
          <div className="dashboard-stats">
            <div className="stat-card">
              <div className="stat-number">{bookings.length}</div>
              <div className="stat-label">Total Bookings</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{pendingBookings.length}</div>
              <div className="stat-label">Pending</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{confirmedCount}</div>
              <div className="stat-label">Confirmed</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{totalRevenueDisplay}</div>
              <div className="stat-label">Total Revenue</div>
            </div>
          </div>
        </div>

        <div className="bookings-section">
          <h2 className="section-title">Recent Bookings</h2>
          {fetchError && (
            <div className="admin-error">{fetchError}</div>
          )}
          {bookings.length === 0 ? (
            <div className="no-bookings">
              <p>No bookings found.</p>
            </div>
          ) : (
            <div>
              {pendingBookings.length > 0 && (
                <div className="pending-section">
                  <h3>Pending Bookings</h3>
                  <div className="bookings-table">
                    {renderTableHeader()}
                    {pendingBookings.map(renderBookingRow)}
                  </div>
                </div>
              )}

              <div className="all-bookings-section">
                <h3>All Bookings</h3>
                <div className="bookings-table">
                  {renderTableHeader()}
                  {bookings.map(renderBookingRow)}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;